export type RiskLevel = 'high' | 'medium' | 'low';

export type Incident = {
  id: string;
  title: string;
  category: string;
  categoryName: string;
  riskLevel: RiskLevel;
  date: string;
  platform: string;
  summary: string;
  cover: string;
  tags: string[];
  readMinutes: number;
  facts: string[];
  timeline: { date: string; text: string }[];
  sections: { heading: string; paragraphs: string[] }[];
  advice: string[];
};

export const riskLevelMap: Record<RiskLevel, { label: string; className: string }> = {
  high: { label: '高风险', className: 'bg-primary-500 text-background-50' },
  medium: { label: '中风险', className: 'bg-accent-500 text-foreground-950' },
  low: { label: '低风险', className: 'bg-secondary-500 text-background-50' },
};

export const incidents: Incident[] = [
  {
    id: 'inc-01',
    title: '某会员制电商数据库遭未授权访问，超百万条收货信息被挂售',
    category: 'data-breach',
    categoryName: '数据泄露',
    riskLevel: 'high',
    date: '2026-08-18',
    platform: '某会员制电商平台',
    summary:
      '安全研究人员在公开渠道发现一批包含姓名、手机号与收货地址的数据样本，经比对与该平台会员信息高度吻合，平台随后确认存在未授权访问记录。',
    cover:
      'https://readdy.ai/api/search-image?query=Moody%20editorial%20illustration%20of%20glowing%20data%20packets%20escaping%20a%20dark%20server%20room%2C%20rust%20red%20and%20amber%20accents%2C%20minimal%20clean%20background%2C%20serious%20investigative%20tone&width=1200&height=750&seq=inc-breach-01&orientation=landscape',
    tags: ['收货信息', '未授权访问', '数据挂售'],
    readMinutes: 6,
    facts: [
      '外泄字段以姓名、手机号、收货地址为主，未包含完整银行卡信息',
      '样本规模约在百万条量级，字段结构一致，判断为同一次导出',
      '平台在收到通报后关闭了受影响的旧版接口',
    ],
    timeline: [
      { date: '08-14', text: '研究者在数据交易频道发现该批样本，并向平台通报。' },
      { date: '08-16', text: '平台确认旧版订单查询接口存在鉴权缺失，可被枚举访问。' },
      { date: '08-18', text: '平台下线相关接口并对外发布说明，提示用户警惕仿冒客服。' },
    ],
    sections: [
      {
        heading: '事件是怎么发生的',
        paragraphs: [
          '问题并不在支付系统，而是出在一个被遗忘的旧版订单查询接口。按照平台说明，该接口在迁移后长期未下线，且只依赖一个顺序可枚举的订单编号作为访问凭证，没有做登录态与服务端权限二次校验。',
          '这意味着攻击者不需要拿到任何账号密码，只要按规则生成编号，就能批量读取订单中的收货人信息。这类「越权读取」是数据泄露中最被低估的一类成因，它不依赖漏洞利用工具，也很难在业务日志中被第一时间察觉。',
        ],
      },
      {
        heading: '为什么收货信息值得重视',
        paragraphs: [
          '很多人认为「只有地址和电话，问题不大」。但收货信息恰恰是精准诈骗最有效的原料：不法分子可以准确报出你最近购买的商品、下单时间与收货地址，从而大幅提升假客服话术的可信度。',
          '在本次事件中，已经出现用户反映收到自称「平台售后」的来电，能够完整复述订单信息并要求下载「理赔」应用。这就是典型的「数据泄露 → 精准诈骗」链条。',
        ],
      },
    ],
    advice: [
      '若你在该平台留有常用收货地址，建议后续改用代收点或快递柜，减少精确门牌信息暴露',
      '对任何能准确报出你订单细节的来电保持警惕，官方售后不会要求你下载非官方应用',
      '检查该账号是否与其他重要账号共用同一手机号 + 密码组合，避免被撞库连带影响',
    ],
  },
  {
    id: 'inc-02',
    title: '在线教育应用被通报超范围索取权限，通讯录与相册权限遭质疑',
    category: 'illegal-collection',
    categoryName: '违规收集使用',
    riskLevel: 'medium',
    date: '2026-08-09',
    platform: '某在线教育类应用',
    summary:
      '专项检测通报显示，该应用在用户未使用相关功能时仍频繁读取通讯录与相册，且首次启动时以「不授权无法使用」的方式强制索权。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20phone%20screen%20surrounded%20by%20floating%20permission%20icons%2C%20warm%20amber%20and%20neutral%20tones%2C%20minimal%20clean%20background%2C%20professional%20serious%20style&width=1200&height=750&seq=inc-permission-01&orientation=landscape',
    tags: ['超范围索权', '强制授权', '专项通报'],
    readMinutes: 5,
    facts: [
      '检测覆盖冷启动、后台驻留与功能使用三个场景',
      '通报要求限期整改，并明确不得因用户拒绝非必要权限而限制核心功能',
      '应用商店已对相关版本进行提示标注',
    ],
    timeline: [
      { date: '08-02', text: '专项检测机构发布本批次应用隐私合规检测结果。' },
      { date: '08-05', text: '平台回应称部分权限读取源于第三方统计组件，将一并整改。' },
      { date: '08-09', text: '应用商店更新隐私标签，标注该应用存在权限使用异常记录。' },
    ],
    sections: [
      {
        heading: '「不用也读」比「强制授权」更隐蔽',
        paragraphs: [
          '强制索权用户是能感知的——你点了「不允许」，应用就弹窗拦住你。真正难察觉的是另一种情况：你在用它的课程播放功能，它却在后台读取你的通讯录与相册。',
          '本次通报正是针对后者。检测发现，该应用在未触发任何相关功能的场景下依然存在高频读取行为，且这些读取与业务说明中声明的用途无法对应。',
        ],
      },
      {
        heading: '授权是可以收回的',
        paragraphs: [
          '多数用户并不清楚权限可以随时收回。以主流手机系统为例，在系统设置的应用详情里可以单独关闭通讯录、相册、位置等权限，也可以把位置权限改为「仅使用时允许」。',
          '对于确实影响到功能的权限，可以先关闭再观察是否影响正常使用；如果应用因此直接闪退或反复弹窗，那本身就是需要警惕的信号。',
        ],
      },
    ],
    advice: [
      '进入系统设置，逐个核对该应用已获得的权限，关闭与当前功能无关的授权',
      '把位置权限调整为「仅使用时允许」，关闭「精确位置」',
      '在系统隐私设置中查看「最近访问记录」，识别高频读取敏感权限的应用',
    ],
  },
  {
    id: 'inc-03',
    title: '免密支付被默认开启导致连续扣款，多起盗刷投诉指向同一代扣通道',
    category: 'fraud',
    categoryName: '盗刷与诈骗',
    riskLevel: 'high',
    date: '2026-07-28',
    platform: '某会员订阅服务平台',
    summary:
      '大量用户反映在不知情的情况下被连续多月扣款，核查发现订阅协议中的免密代扣条款以小字默认勾选，且取消入口被深埋在多个层级菜单中。',
    cover:
      'https://readdy.ai/api/search-image?query=Dark%20editorial%20concept%20illustration%20of%20a%20credit%20card%20and%20recurring%20payment%20arrows%2C%20rust%20red%20and%20amber%20tones%2C%20minimal%20clean%20background%2C%20serious%20tone&width=1200&height=750&seq=inc-deduct-01&orientation=landscape',
    tags: ['免密代扣', '自动续费', '取消困难'],
    readMinutes: 6,
    facts: [
      '投诉集中在「未曾主动续费却被扣款」与「找不到取消入口」两类',
      '代扣协议在首次下单页以默认勾选方式呈现，未做显著提示',
      '平台后续调整了取消路径，但仍需进入两层以上菜单',
    ],
    timeline: [
      { date: '07-20', text: '用户投诉量在短期内明显上升，指向同一支付代扣通道。' },
      { date: '07-24', text: '平台承认存在默认勾选续费协议的情况，承诺优化提示。' },
      { date: '07-28', text: '平台将取消入口前移至账户页，并对近三个月误扣订单开放退款申请。' },
    ],
    sections: [
      {
        heading: '盗刷未必发生在账号被盗之后',
        paragraphs: [
          '很多人把「盗刷」等同于账号被盗。但在本次事件中，绝大多数用户的账号没有任何异常登录记录——钱是通过用户自己曾经「授权」的代扣协议正常扣走的。',
          '问题出在授权的质量上：协议藏在小字里、默认勾选、取消路径复杂。这三个设计叠加，就构成了一种事实上的「付费陷阱」。',
        ],
      },
      {
        heading: '如何快速排查自己的代扣',
        paragraphs: [
          '主流支付工具都提供「自动续费 / 免密支付」管理入口，通常位于「我的 - 支付设置」或「钱包 - 扣费服务」中。定期打开看一眼，往往能发现已经不再使用的订阅。',
          '删除应用并不等于取消订阅。这是用户最容易误解的一点：卸载只清理了本地客户端，服务端保存的代扣授权仍然有效，会继续按期扣款。',
        ],
      },
    ],
    advice: [
      '打开支付工具的「自动续费 / 免密支付」列表，逐项确认是否还需要',
      '不用的订阅先取消协议，再卸载应用，顺序不要调换',
      '为小额订阅单独使用一张余额较低的支付方式，控制单次损失上限',
    ],
  },
  {
    id: 'inc-04',
    title: '订单接口越权漏洞致他人收货信息可被枚举读取，平台两天内完成修复',
    category: 'vulnerability',
    categoryName: '安全漏洞',
    riskLevel: 'medium',
    date: '2026-07-15',
    platform: '某本地生活服务平台',
    summary:
      '白帽研究者发现订单详情接口缺少服务端权限校验，仅凭可预测的订单号即可读取他人订单，漏洞在通报后 48 小时内被修复。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20a%20chain%20link%20breaking%20inside%20a%20digital%20grid%2C%20amber%20and%20rust%20accents%2C%20minimal%20clean%20background%2C%20technical%20serious%20tone&width=1200&height=750&seq=inc-idor-01&orientation=landscape',
    tags: ['越权访问', '接口鉴权', '48小时修复'],
    readMinutes: 5,
    facts: [
      '漏洞类型属于失效的访问控制（越权读取）',
      '研究者未下载超出验证需要的数据，漏洞未被公开利用',
      '平台补充了服务端校验规则并增加异常访问告警',
    ],
    timeline: [
      { date: '07-13', text: '研究者向平台安全响应渠道提交漏洞详情与复现步骤。' },
      { date: '07-14', text: '平台确认问题并临时限制接口访问频率。' },
      { date: '07-15', text: '修复验证通过，接口增加登录态与归属校验。' },
    ],
    sections: [
      {
        heading: '一个接口的三道门',
        paragraphs: [
          '一个订单查询接口至少要过三道关：你是谁（身份认证）、这个订单是不是你的（归属校验）、你以什么频率访问（限流）。本次漏洞缺的是第二道。',
          '缺少归属校验时，前端界面看起来一切正常——你在自己的订单页里只能看到自己的订单。但只要把请求里的订单号改一改，服务端就会把别人的数据原样返回。',
        ],
      },
      {
        heading: '快速修复也是一种能力',
        paragraphs: [
          '值得肯定的是响应速度。从通报到修复验证通过不到 48 小时，且平台建立了对外可用的安全响应渠道，研究者知道该把问题交给谁。',
          '对用户而言，判断一个平台是否值得信任，不只看它有没有出过问题，更要看它出问题后的响应速度与透明度。',
        ],
      },
    ],
    advice: [
      '在评价平台安全性时，把「漏洞响应速度」纳入考量，而不仅是「是否出过漏洞」',
      '如发现异常可见他人信息，第一时间截图记录并向平台安全渠道反馈，不要继续遍历',
    ],
  },
  {
    id: 'inc-05',
    title: '健康打卡类小程序被指默认共享数据给第三方，用户画像被用于精准营销',
    category: 'privacy',
    categoryName: '隐私风险',
    riskLevel: 'medium',
    date: '2026-06-30',
    platform: '某健康管理类小程序',
    summary:
      '用户发现隐私政策中写明「可能与合作方共享去标识化信息」，但实际共享范围包含打卡记录与体重变化等高频数据，且关闭入口极不显眼。',
    cover:
      'https://readdy.ai/api/search-image?query=Conceptual%20illustration%20of%20abstract%20human%20silhouette%20made%20of%20data%20points%20being%20shared%20outward%2C%20muted%20teal%20and%20warm%20neutral%20palette%2C%20minimal%20clean%20background&width=1200&height=750&seq=inc-share-01&orientation=landscape',
    tags: ['第三方共享', '用户画像', '默认开启'],
    readMinutes: 5,
    facts: [
      '共享字段包含打卡频次、体重区间等可关联到个人的高频数据',
      '隐私政策中虽有提及，但未在功能页做二次提示',
      '平台在收到反馈后增加了「营销用途」独立开关',
    ],
    timeline: [
      { date: '06-24', text: '用户发布对比截图，指出关闭入口隐蔽且文案含糊。' },
      { date: '06-27', text: '平台说明共享数据已去标识化，但仍承诺优化提示。' },
      { date: '06-30', text: '新增独立开关，默认关闭营销用途共享。' },
    ],
    sections: [
      {
        heading: '「去标识化」不等于「与你无关」',
        paragraphs: [
          '去标识化意味着数据里不再直接包含你的姓名和手机号，但打卡时间、体重区间、设备信息这些组合起来，依然具备很高的可识别性。',
          '换句话说，去标识化降低了直接泄露的风险，但并不等于这些数据无法被用来描绘你。这一点在隐私政策里往往被一笔带过。',
        ],
      },
      {
        heading: '默认开启是问题的核心',
        paragraphs: [
          '如果一项数据共享默认开启，而关闭它需要用户主动寻找并点击三次以上，那么大多数用户实际上从未做出选择。',
          '真正合规的做法是把涉及营销的共享设置为默认关闭，由用户主动开启。本次事件整改后正是这个方向。',
        ],
      },
    ],
    advice: [
      '在小程序的设置页查找「个性化推荐 / 营销用途」类开关，确认是否默认开启',
      '谨慎填写体重、作息、心理状态等高频健康数据，除非确有必要',
      '定期在微信或系统的授权管理中清理不再使用的小程序授权',
    ],
  },
  {
    id: 'inc-06',
    title: '某招聘平台因未明示使用目的收集简历信息被公开发布整改通报',
    category: 'penalty',
    categoryName: '监管处罚',
    riskLevel: 'low',
    date: '2026-06-12',
    platform: '某招聘信息平台',
    summary:
      '公开通报指出该平台收集求职者简历信息时未逐一说明使用目的，也未提供便捷的撤回同意方式，被要求限期整改并接受复核。',
    cover:
      'https://readdy.ai/api/search-image?query=Editorial%20illustration%20of%20official%20document%20with%20seal%20and%20digital%20tablet%2C%20muted%20teal%20and%20warm%20beige%20palette%2C%20minimal%20clean%20background%2C%20formal%20serious%20tone&width=1200&height=750&seq=inc-notice-01&orientation=landscape',
    tags: ['公开通报', '未明示目的', '限期整改'],
    readMinutes: 4,
    facts: [
      '通报关注的是信息处理的告知与同意环节，而非数据泄露',
      '平台需在限期内提交整改报告并接受复核',
      '通报属于公开监管信息，可在官方渠道查询原文',
    ],
    timeline: [
      { date: '06-05', text: '监管机构发布本批次通报，涉及多个应用。' },
      { date: '06-08', text: '平台回应已上线新的授权说明页与撤回入口。' },
      { date: '06-12', text: '通报对外公示，进入整改复核阶段。' },
    ],
    sections: [
      {
        heading: '被通报不等于有恶意',
        paragraphs: [
          '通报的核心是「程序没做对」，而不是「故意偷数据」。简历投递涉及大量个人信息，收集时必须逐一说明用途，并提供便捷的撤回方式。',
          '平台在改版频繁的产品中很容易忽略这些说明，但忽略本身就是合规风险。',
        ],
      },
      {
        heading: '为什么监管记录值得单独看',
        paragraphs: [
          '厂商自己写的隐私政策，是用来自我描述的；而监管通报是第三方视角的记录。两者的可信度不在一个层级。',
          '在评估一个平台时，可以优先检索它是否有过公开通报或处罚记录，这比读它的官网承诺更直接。',
        ],
      },
    ],
    advice: [
      '投递简历前，留意平台是否说明「谁能看到你的简历」以及如何撤回',
      '定期检查招聘平台中已投递与已开放状态的简历，关闭长期开放的简历可见性',
    ],
  },
  {
    id: 'inc-07',
    title: '第三方统计组件被曝可读取剪贴板，多家应用紧急下架更新',
    category: 'vulnerability',
    categoryName: '安全漏洞',
    riskLevel: 'medium',
    date: '2026-05-22',
    platform: '某开发者统计组件',
    summary:
      '安全团队披露某统计组件存在读取剪贴板与收集设备标识的行为，该组件被多个应用集成，随后多家厂商宣布紧急更新或暂时下架。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20modular%20code%20blocks%20with%20one%20glowing%20red%20block%2C%20amber%20and%20neutral%20tones%2C%20minimal%20clean%20background%2C%20serious%20editorial%20style&width=1200&height=750&seq=inc-sdk-01&orientation=landscape',
    tags: ['第三方组件', '供应链', '剪贴板'],
    readMinutes: 6,
    facts: [
      '问题组件被集成在多个应用内，影响面取决于具体集成方式',
      '剪贴板中可能包含验证码、口令等短时敏感内容',
      '主要厂商在数日内完成版本替换或下架',
    ],
    timeline: [
      { date: '05-18', text: '安全团队发布分析报告，披露组件异常行为。' },
      { date: '05-20', text: '组件方发布声明称相关行为已下线。' },
      { date: '05-22', text: '多家应用宣布更新版本，部分应用暂时下架。' },
    ],
    sections: [
      {
        heading: '供应链风险：你没写的代码也在跑',
        paragraphs: [
          '应用里通常集成了大量第三方组件。这些组件由外部团队维护，一旦其中某个组件行为越界，问题就会「传染」给所有集成它的应用。',
          '本次事件正是如此：出问题的不是应用本身，而是它们共同依赖的一个组件。',
        ],
      },
      {
        heading: '剪贴板为什么敏感',
        paragraphs: [
          '剪贴板里经常短暂停留验证码、临时口令、链接等敏感信息。读取剪贴板本身可以有正当用途，但在无用户操作时静默读取，就明显越界。',
          '这也是为什么主流系统近年来都会在应用读取剪贴板时给出顶部提示——让行为变得可见，本身就是一种约束。',
        ],
      },
    ],
    advice: [
      '收到验证码后，尽量手动输入或在粘贴后立即清除剪贴板',
      '关注应用更新日志中「移除某第三方组件」的说明，及时升级',
    ],
  },
  {
    id: 'inc-08',
    title: '仿冒客服引导远程共享屏幕，多地用户在同一周内报案',
    category: 'fraud',
    categoryName: '盗刷与诈骗',
    riskLevel: 'high',
    date: '2026-05-06',
    platform: '仿冒客服团伙',
    summary:
      '诈骗团伙以「订单异常需退款」为由，诱导用户下载远程协助类应用并开启屏幕共享，随后在用户视线内完成转账操作。',
    cover:
      'https://readdy.ai/api/search-image?query=Dark%20editorial%20illustration%20of%20a%20phone%20with%20screen%20sharing%20icon%20and%20warning%20glow%2C%20rust%20red%20and%20amber%20accents%2C%20minimal%20clean%20background%2C%20serious%20tone&width=1200&height=750&seq=inc-screen-01&orientation=landscape',
    tags: ['屏幕共享', '仿冒客服', '退款诈骗'],
    readMinutes: 5,
    facts: [
      '话术统一，均能准确报出用户近期订单信息',
      '要求用户下载非官方渠道的「会议 / 协助」类应用',
      '一旦开启屏幕共享，验证码与余额会被实时看到',
    ],
    timeline: [
      { date: '04-29', text: '首批用户报案，描述手法高度一致。' },
      { date: '05-02', text: '平台发布风险提示，强调官方客服不会要求远程共享。' },
      { date: '05-06', text: '多地反诈中心发布同类预警，提醒公众注意。' },
    ],
    sections: [
      {
        heading: '为什么这个套路屡屡得手',
        paragraphs: [
          '骗子掌握了你真实的订单信息，这是信任的起点；退款是你本来就期待的事，这是动机；「下载一个应用配合操作」听起来只是流程繁琐，这是合理化的借口。',
          '当屏幕共享开启后，你手机上的验证码、银行余额、操作界面全部实时可见。骗子不需要你的密码，只需要你在他「指导」下自己完成转账。',
        ],
      },
      {
        heading: '一条几乎不会误伤的判断标准',
        paragraphs: [
          '任何要求你开启屏幕共享、远程协助，或索要短信验证码的「客服」，都可以直接判定为诈骗。正规平台退款走原路返回，不需要你配合任何远程操作。',
          '担心误伤的话，挂断后自己通过官方应用内的客服入口重新联系，就足以确认。',
        ],
      },
    ],
    advice: [
      '牢记一条铁律：客服不会要求屏幕共享，也不会索要验证码',
      '不安装来源不明的「会议 / 协助」类应用',
      '若已开启共享，立即关闭网络并联系银行冻结卡片，随后报案',
    ],
  },
];

export const findIncidentById = (id: string): Incident | undefined =>
  incidents.find((item) => item.id === id);

export const incidentsByTopic = (slug: string): Incident[] =>
  incidents.filter((item) => item.category === slug);